import dayjs from 'dayjs'
import { parseRecurringTaskDeadlineDefaultValue } from '../lib/taskTrackerRecurringTaskDeadline.utils'
import { normalizeRecurringTaskTime } from '../lib/taskTrackerRecurringTasks.utils'
import type {
  TaskTrackerRecurringTask,
  TaskTrackerRecurringTaskSavePayload,
  TaskTrackerRecurringTaskStickerDefault,
} from '../lib/taskTrackerRecurringTasks.types'
import type {
  ApiTaskTrackerRecurringTask,
  ApiTaskTrackerRecurringTaskSaveBody,
  ApiTaskTrackerRecurringTaskStickerDefault,
} from './taskTracker.recurringTasks.api.types'

const mapStickerDefaultFromApi = (
  sticker: ApiTaskTrackerRecurringTaskStickerDefault,
): TaskTrackerRecurringTaskStickerDefault | null => {
  const fieldSettingId = Number(sticker.field_setting_id)
  if (!Number.isFinite(fieldSettingId)) return null

  const performerValues = Array.isArray(sticker.values)
    ? sticker.values
        .map(Number)
        .filter((userId) => Number.isFinite(userId) && userId > 0)
        .map(String)
    : []

  if (performerValues.length > 0) {
    return {
      fieldSettingId,
      value: null,
      values: performerValues,
    }
  }

  if (sticker.value == null || sticker.value === undefined) {
    return {
      fieldSettingId,
      value: null,
    }
  }

  return {
    fieldSettingId,
    value: String(sticker.value),
  }
}

export const mapRecurringTaskFromApi = (
  item: ApiTaskTrackerRecurringTask,
): TaskTrackerRecurringTask => ({
  id: Number(item.id),
  boardId: Number(item.board_id),
  title: String(item.title ?? '').trim(),
  interval: item.interval,
  time: normalizeRecurringTaskTime(String(item.time ?? '12:00')),
  weekDays: Array.isArray(item.week_days)
    ? item.week_days.map(Number).filter((day) => Number.isFinite(day))
    : [],
  monthDay:
    item.month_day == null || item.month_day === undefined
      ? null
      : Number(item.month_day),
  yearMonth:
    item.year_month == null || item.year_month === undefined
      ? null
      : Number(item.year_month),
  yearDay:
    item.year_day == null || item.year_day === undefined
      ? null
      : Number(item.year_day),
  startDate: String(item.start_date ?? '').trim() || dayjs().format('YYYY-MM-DD'),
  endDate:
    item.end_date == null || item.end_date === undefined
      ? null
      : String(item.end_date).trim() || null,
  columnId:
    item.column_id == null || item.column_id === undefined
      ? null
      : Number(item.column_id),
  defaultStickerValues: Array.isArray(item.default_sticker_values)
    ? item.default_sticker_values
        .map(mapStickerDefaultFromApi)
        .filter(
          (sticker): sticker is TaskTrackerRecurringTaskStickerDefault =>
            sticker != null,
        )
    : [],
  createdAt: Number(item.created_at ?? 0),
  updatedAt: Number(item.updated_at ?? 0),
})

export const mapRecurringTasksFromApi = (
  items: ApiTaskTrackerRecurringTask[] | null | undefined,
): TaskTrackerRecurringTask[] =>
  (items ?? []).map(mapRecurringTaskFromApi)

const mapStickerDefaultToApi = (
  item: TaskTrackerRecurringTaskStickerDefault,
): ApiTaskTrackerRecurringTaskStickerDefault => {
  const performerIds = (item.values ?? [])
    .map(Number)
    .filter((userId) => Number.isFinite(userId) && userId > 0)

  if (performerIds.length > 0) {
    return {
      field_setting_id: item.fieldSettingId,
      values: performerIds,
    }
  }

  const deadlineOffset = parseRecurringTaskDeadlineDefaultValue(item.value)
  if (deadlineOffset != null) {
    return {
      field_setting_id: item.fieldSettingId,
      value: deadlineOffset,
    }
  }

  return {
    field_setting_id: item.fieldSettingId,
    value: item.value ?? null,
  }
}

export const mapRecurringTaskSavePayloadToApi = (
  payload: TaskTrackerRecurringTaskSavePayload,
): ApiTaskTrackerRecurringTaskSaveBody => {
  const defaultStickerValues = (payload.defaultStickerValues ?? []).map(
    mapStickerDefaultToApi,
  )

  const body: ApiTaskTrackerRecurringTaskSaveBody = {
    title: payload.title.trim(),
    interval: payload.interval,
    time: normalizeRecurringTaskTime(payload.time),
    start_date: payload.startDate,
    end_date: payload.endDate ?? null,
    column_id: payload.columnId,
    ...(defaultStickerValues.length > 0
      ? { default_sticker_values: defaultStickerValues }
      : {}),
  }

  if (payload.interval === 'weekly') {
    body.week_days = payload.weekDays ?? []
  }

  if (payload.interval === 'monthly') {
    body.month_day = payload.monthDay ?? null
  }

  if (payload.interval === 'yearly') {
    body.year_month = payload.yearMonth ?? null
    body.year_day = payload.yearDay ?? null
  }

  return body
}
